import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

import '../landing/landing.css';

// Shared shell for /privacy and /terms. Rides on the landing's scoped .lp
// styles so the legal pages read as part of the marketing site, and are
// reachable without signing in — Google's OAuth verification crawls them.
const CONTAINER = 'mx-auto w-full max-w-[760px] px-6';

export const CONTACT_EMAIL = 'contact@enflux.cc';

interface LegalLayoutProps {
	title: string;
	effectiveDate: string;
	children: ReactNode;
}

export function LegalLayout({ title, effectiveDate, children }: LegalLayoutProps) {
	return (
		<div className="lp min-h-screen">
			<header className="border-b border-white/[0.08]">
				<div className="mx-auto flex h-[76px] w-full max-w-[1240px] items-center justify-between px-6">
					<Link to="/" className="flex items-center">
						<img src="/enflux-logo.png" alt="Enflux" className="h-[26px] w-auto" />
					</Link>
					<Link to="/sign-in" className="lp-btn lp-btn-primary lp-btn-sm">
						Sign in
						<ArrowUpRight className="h-3.5 w-3.5" />
					</Link>
				</div>
			</header>

			<main className={`${CONTAINER} py-16`}>
				<h1 className="lp-h2">{title}</h1>
				<p className="mt-3 text-[13px] text-white/40">Effective {effectiveDate}</p>
				<div className="mt-10 space-y-10 text-[15px] leading-relaxed text-white/[0.62]">
					{children}
				</div>
			</main>

			<footer className="border-t border-white/[0.08]">
				<div className="mx-auto flex w-full max-w-[1240px] flex-col items-center justify-between gap-3 px-6 py-6 text-[12px] text-white/[0.35] sm:flex-row">
					<span>Enflux © {new Date().getFullYear()}</span>
					<nav className="flex items-center gap-5">
						<Link to="/privacy" className="transition-colors hover:text-white/75">
							Privacy Policy
						</Link>
						<Link to="/terms" className="transition-colors hover:text-white/75">
							Terms of Service
						</Link>
						<a href={`mailto:${CONTACT_EMAIL}`} className="transition-colors hover:text-white/75">
							{CONTACT_EMAIL}
						</a>
					</nav>
				</div>
			</footer>
		</div>
	);
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
	return (
		<section className="space-y-4">
			<h2 className="lp-h3 text-[1.2rem]">{title}</h2>
			{children}
		</section>
	);
}

export function ExtLink({ href, children }: { href: string; children: ReactNode }) {
	return (
		<a href={href} target="_blank" rel="noreferrer" className="text-[#00c7f9] hover:underline">
			{children}
		</a>
	);
}
